/* eslint-disable no-unused-vars */

/* eslint-disable react/prop-types */

/* eslint-disable react/display-name */
import PropTypes from 'prop-types';
import React, { forwardRef, useCallback, useMemo } from 'react';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { isMobile } from 'react-device-detect';
import styled, { css } from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { theme } from '_constants/theme';

import Icon from 'components/atomic/Icon';

const { colors, fontSizes, spaces } = theme;

const StyledGroup = styled(forwardRef(({ bottomSpace, ...props }, ref) => <Form.Group ref={ref} {...props} />))`
  ${({ bottomSpace }) =>
    bottomSpace &&
    css`
      margin-bottom: ${typeof bottomSpace === 'string' ? bottomSpace : `${bottomSpace}px`};
    `}
`;

const StyledLabel = styled(forwardRef(({ color, disabled, ...props }, ref) => <Form.Label ref={ref} {...props} />))`
  display: flex;
  align-items: flex-start;
  align-items: center;
  flex-wrap: nowrap;

  margin-bottom: 0;

  font-family: 'Inter';
  font-size: ${theme.fontSizes.smallX}px;
  font-style: normal;
  font-weight: 600;
  color: ${theme.colors.gray.secondary};

  cursor: pointer;

  ${({ color }) =>
    color &&
    css`
      color: ${color};
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
    `}
`;

const StyledIconContainer = styled(forwardRef(({ color, borderColor, ...props }, ref) => <div ref={ref} {...props} />))`
  display: flex;
  justify-content: center;
  align-items: center;

  height: ${theme.spaces.medium}px;
  width: ${theme.spaces.medium}px;
  min-height: ${theme.spaces.medium}px;
  min-width: ${theme.spaces.medium}px;

  margin-right: ${theme.spaces.small - 2}px;

  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.gray.secondary};
  border-radius: 4px;

  ${({ color }) =>
    color &&
    css`
      background: ${color};
    `}

  ${({ borderColor }) =>
    borderColor &&
    css`
      border-color: ${borderColor};
    `}
`;

const StyledFeedback = styled(forwardRef(({ color, ...props }, ref) => <Form.Control.Feedback ref={ref} {...props} />))`
  padding-left: ${spaces.smallest}px;

  font-family: 'Inter';
  font-size: ${fontSizes.smallX}px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;

  &.processing-feedback {
    color: ${theme.colors.gray.secondary};

    ${({ color }) =>
      color &&
      css`
        color: ${color};
      `};
  }

  &.valid-feedback {
    color: ${theme.colors.green.primary};
  }

  &.invalid-feedback {
    color: ${theme.colors.red.primary};
  }
`;

const Checkbox = ({
  name,
  label = 'Label',
  hint,
  disabled,
  bottomSpace,
  textColor,
  tickColor,
  boxColor,
  boxBorderColor,
  valid,
  invalid,
  feedback,
  value = false,
  onChange,
  ...restProps
}) => {
  const handleChange = useCallback(
    (e) => {
      const { name, checked } = e.target;
      onChange && onChange({ target: { name, value: checked } });
    },
    [onChange]
  );

  const overlayTriggerProps = useMemo(() => {
    const props = {
      placement: isMobile ? 'top' : 'right-start',
      delay: { show: 0, hide: 0 },
      trigger: ['hover', 'focus'],
      overlay: (
        <Tooltip id={`tooltip-${name}`} className="hintTooltip">
          {hint}
        </Tooltip>
      ),
    };

    if (!hint || isMobile) {
      props.show = false;
    }

    return props;
  }, [name, hint]);

  return (
    <OverlayTrigger {...overlayTriggerProps}>
      <StyledGroup bottomSpace={bottomSpace}>
        <StyledLabel htmlFor={name} color={textColor} disabled={disabled} data-cy={name}>
          <Form.Check
            {...restProps}
            hidden
            id={name}
            type="checkbox"
            name={name}
            disabled={disabled}
            checked={value}
            onChange={handleChange}
          />
          <StyledIconContainer color={value ? boxColor : undefined} borderColor={boxBorderColor}>
            {value && <Icon alias={ICON_ALIASES.tick} color={tickColor || theme.colors.gray.secondary} />}
          </StyledIconContainer>
          {label}
        </StyledLabel>
        <StyledFeedback type="processing" visible={!valid && !invalid && feedback}>
          {feedback}
        </StyledFeedback>
        <StyledFeedback type="valid" visible={valid}>
          {feedback}
        </StyledFeedback>
        <StyledFeedback type="invalid" visible={invalid}>
          {feedback}
        </StyledFeedback>
      </StyledGroup>
    </OverlayTrigger>
  );
};

Checkbox.propTypes = {
  name: PropTypes.string,
  label: PropTypes.node,
  hint: PropTypes.node,
  disabled: PropTypes.bool,
  bottomSpace: PropTypes.number,
  textColor: PropTypes.string,
  tickColor: PropTypes.string,
  boxColor: PropTypes.string,
  boxBorderColor: PropTypes.string,
  valid: PropTypes.bool,
  invalid: PropTypes.bool,
  feedback: PropTypes.node,
  value: PropTypes.bool,
  onChange: PropTypes.func,
};

export default Checkbox;
